import { useContext } from "react";
import { Controller } from "react-hook-form";
import { Alert, Button, Col, Form, Input, Row } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import Text from "antd/es/typography/Text";
import { InboxOutlined, TrophyOutlined } from '@ant-design/icons';

import { IGiveawayAuto, IGiveawayHistory, IPanelUser } from "../../../../../interfaces";
import useImportData from "./useImportData";
import FileAndHashContainer from "../../components/FileAndHashContainer";
import { formatDateStringToDateAndTime } from "../../../../../utils/Conversores";
import { ThemeContext } from "../../../../../contexts/ThemeContext";

interface IProps {
    giveawayAuto: IGiveawayAuto | undefined;
    lastGiveawayHistory: IGiveawayHistory | undefined;
    panelUserUploadedWinnersFile?: IPanelUser;
    onWinnersFileImported: (hash: string, file: string) => void;
}

export default function ImportData({
    giveawayAuto,
    lastGiveawayHistory,
    panelUserUploadedWinnersFile,
    onWinnersFileImported
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isSubmiting,
        control,
        handleSubmit,
        handleSubmitForm,
        errors,
        dummyRequest,
        handleChangeWinnersFile,
        beingUploadedWinnersFileName,
        file,
        handleClickDownloadFile,
        hash,
        isDownloadingFile,
        ACCEPTED_FILE_TYPES,
    } = useImportData({ giveawayAuto, lastGiveawayHistory, onWinnersFileImported });

    return (
        <div
            style={{
                background: isDark ? 'rgba(82, 196, 26, 0.04)' : '#f4fbf5',
                border: `1px solid ${isDark ? 'rgba(82, 196, 26, 0.18)' : '#d3f2db'}`,
                borderRadius: 12,
                padding: '20px 24px',
            }}
        >
            <Form
                onFinish={handleSubmit(handleSubmitForm)}
                autoComplete="off"
                layout="vertical"
            >
                {!file && !hash && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <Alert
                            message="Importação do Resultado da Apuração"
                            description="Faça o upload do arquivo criptografado de ganhadores gerado pelo sistema de sorteio automático e informe o hash de validação correspondente."
                            type="success"
                            showIcon
                            icon={<TrophyOutlined style={{ color: '#52c41a' }} />}
                            style={{
                                borderRadius: 10,
                                background: isDark ? 'rgba(82, 196, 26, 0.08)' : '#ffffff',
                                border: `1px solid ${isDark ? 'rgba(82, 196, 26, 0.22)' : '#d9f7be'}`,
                            }}
                        />

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#e2e8f0' : '#135200' }}>
                                            Arquivo dos Ganhadores:
                                        </span>
                                    }
                                    validateStatus={errors.winners_file ? 'error' : undefined}
                                    help={errors.winners_file?.message}
                                    required
                                >
                                    <Controller
                                        name="winners_file"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: "Por favor, selecione o arquivo gerado pelo sistema de sorteio!" }
                                        }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <Dragger
                                                {...field}
                                                showUploadList={false}
                                                customRequest={dummyRequest}
                                                onChange={(e: UploadChangeParam<UploadFile>) => handleChangeWinnersFile({ onChangeHookForm: onChange, file: e.file })}
                                                accept={ACCEPTED_FILE_TYPES.toString()}
                                                style={{
                                                    background: isDark ? '#141414' : '#ffffff',
                                                    borderColor: isDark ? 'rgba(82, 196, 26, 0.3)' : '#b7eb8f',
                                                    borderRadius: 10,
                                                    padding: '16px 0',
                                                }}
                                            >
                                                {beingUploadedWinnersFileName && typeof beingUploadedWinnersFileName === 'string' ? (
                                                    <Text style={{ margin: '0 8px', fontWeight: 600, color: '#52c41a' }}>
                                                        {beingUploadedWinnersFileName}
                                                    </Text>
                                                ) : (
                                                    <>
                                                        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                            <InboxOutlined style={{ color: '#52c41a', fontSize: 36 }} />
                                                        </p>
                                                        <Text style={{ margin: '0 8px', fontSize: 13, color: isDark ? '#d9d9d9' : '#475569' }}>
                                                            Clique ou arraste o arquivo aqui
                                                        </Text>
                                                    </>
                                                )}
                                            </Dragger>
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#e2e8f0' : '#135200' }}>
                                            Hash gerado pelo sistema de sorteio:
                                        </span>
                                    }
                                    required
                                    validateStatus={errors.hash ? 'error' : undefined}
                                    help={errors.hash?.message}
                                >
                                    <Controller
                                        name="hash"
                                        control={control}
                                        rules={{ required: { value: true, message: 'Por favor, informe o Hash!' } }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Cole o código Hash aqui..."
                                                style={{
                                                    height: 38,
                                                    borderRadius: 8,
                                                    fontFamily: 'monospace',
                                                    fontSize: 13,
                                                    background: isDark ? '#141414' : '#ffffff',
                                                    borderColor: isDark ? 'rgba(82, 196, 26, 0.3)' : '#d9f7be',
                                                }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row justify="center" style={{ marginTop: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<TrophyOutlined />}
                                disabled={isSubmiting}
                                loading={isSubmiting}
                                style={{
                                    height: 38,
                                    padding: '0 24px',
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    fontSize: 14,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    background: '#52c41a',
                                    borderColor: '#52c41a',
                                }}
                            >
                                Importar Resultado e Validar
                            </Button>
                        </Row>
                    </div>
                )}

                {file && hash && (
                    <FileAndHashContainer
                        variant="green"
                        hash={hash}
                        file={file}
                        downloadFileButtonLabel="Baixar arquivo de vencedores"
                        fileInfoLabel={
                            panelUserUploadedWinnersFile
                                ? `Arquivo importado em ${formatDateStringToDateAndTime(giveawayAuto?.winners_file_uploaded_at)} por ${panelUserUploadedWinnersFile.name}`
                                : undefined
                        }
                        isDownloadingFile={isDownloadingFile}
                        onClickDownloadFile={handleClickDownloadFile}
                    />
                )}
            </Form>
        </div>
    );
}
